import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { Combobox, userToComboboxOption } from "@/components/ui/Combobox";
import { InfoMessage } from "@/components/ui/InfoMessage";
import { ChevronLeft, Search } from "lucide-react";
import toast from "react-hot-toast";
import { Country, State } from "country-state-city";

const EditSitePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    client: "",
    mine_type: "",
    address: "",
    phone: "",
    country: "",
    state: "",
    status: "operating",
    email: "",
    latitude: "",
    longitude: "",
    contact_name: "",
  });

  const [clients, setClients] = useState([]);
  const [mineTypes, setMineTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [selectedStateName, setSelectedStateName] = useState("");
  // Fetch data on mount
  useEffect(() => {
    setCountries(Country.getAllCountries());

    const fetchClients = async () => {
      try {
        const res = await api.get("/clients/all-active-clients");
        let clientList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          clientList = res.data.data;
        } else if (Array.isArray(res.data)) {
          clientList = res.data;
        }
        setClients(clientList);
      } catch (err) {
        toast.error("Failed to load clients");
        setClients([]);
      }
    };

    const fetchMineTypes = async () => {
      try {
        const res = await api.get("/mine-type?is_active=true&limit=100");
        if (res.data?.data && Array.isArray(res.data.data)) {
          setMineTypes(res.data.data);
        }
      } catch (err) {
        toast.error("Failed to load mine types");
        setMineTypes([]);
      }
    };

    fetchClients();
    fetchMineTypes();
  }, []);

  const handleCountryChange = (value) => {
    const selectedCountry = countries.find((country) => country.isoCode === value);
    const countryName = selectedCountry ? selectedCountry.name : "";
    setFormData((prev) => ({ ...prev, country: countryName, state: "" }));
    setSelectedCountryName(countryName);
    setSelectedStateName("");
    const fetchedStates = State.getStatesOfCountry(value);
    setStates(fetchedStates);
  };

  const handleStateChange = (value) => {
    const selectedState = states.find((state) => state.isoCode === value);
    const stateName = selectedState ? selectedState.name : "";
    setFormData((prev) => ({ ...prev, state: stateName }));
    setSelectedStateName(stateName);
  };

  const { data: siteData, isLoading } = useQuery(
    ["site", id],
    async () => {
      const response = await api.get(`/sites/${id}`);
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        setFormData({
          name: data.name || "",
          client: data.client_id || "",
          mine_type: data.mine_type_name || data.mine_type || "",
          address: data.address || "",
          phone: data.phone || "",
          country: data.country || "",
          state: data.state || "",
          status: data.status || "operating",
          email: data.email || "",
          latitude: data.latitude || "",
          longitude: data.longitude || "",
          contact_name: data.contact_name || "",
          report_names: data.report_names || [],
        });
        setSelectedCountryName(data.country || "");
        setSelectedStateName(data.state || "");

        if (data.country && countries.length > 0) {
          const country = countries.find((c) => c.name === data.country);
          if (country) {
            const fetchedStates = State.getStatesOfCountry(country.isoCode);
            setStates(fetchedStates);
          }
        }
      },
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const updateMutation = useMutation((siteData) => api.put(`/sites/${id}`, siteData), {
    onSuccess: async () => {
      // Invalidate the individual site and any sites lists
      await queryClient.invalidateQueries(["site", id]);
      await queryClient.invalidateQueries(["sites"]);
      // Optionally refetch lists in background to ensure UI shows latest immediately
      queryClient.refetchQueries(["sites"], { active: false });

      toast.success("Site updated successfully");
      navigate("/site");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update site");
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if all required fields are empty
    const allFieldsEmpty =
      !formData.name.trim() &&
      !formData.client &&
      !formData.mine_type.trim() &&
      !formData.address.trim() &&
      !formData.email.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before updating");
      return;
    }

    // Check individual fields and show specific messages
    let hasErrors = false;

    if (!formData.name.trim()) {
      toast.error("Site name is required");
      hasErrors = true;
    }

    if (!formData.client) {
      toast.error("Please select Client");
      hasErrors = true;
    }

    if (!formData.mine_type.trim()) {
      toast.error("please select Mine Type");
      hasErrors = true;
    }

    if (!formData.address.trim()) {
      toast.error("Address is required");
      hasErrors = true;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      hasErrors = true;
    }

    if (!formData.country.trim()) {
      toast.error("Country is required");
      hasErrors = true;
    }

    // If there are validation errors, don't submit
    if (hasErrors) {
      return;
    }

    const submitData = {
      name: formData.name,
      client: formData.client,
      mine_type: formData.mine_type || null,
      address: formData.address,
      phone: formData.phone.trim() || null,
      country: formData.country || null,
      state: formData.state || null,
      status: formData.status,
      email: formData.email,
      contact_name: formData.contact_name || null,
      latitude:
        formData.latitude && formData.latitude !== "1" ? parseFloat(formData.latitude) : null,
      longitude:
        formData.longitude && formData.longitude !== "1" ? parseFloat(formData.longitude) : null,
    };

    updateMutation.mutate(submitData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-b-2 rounded-full border-primary animate-spin"></div>
        <span className="ml-3 text-gray-600">Loading site...</span>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-full">

        <div className="slide-up">
          <form onSubmit={handleSubmit} noValidate className="">
            <div>
              {formData.report_names && formData.report_names.length > 0 && (
                <InfoMessage>
                  Cannot delete this site while it is linked to reports.
                </InfoMessage>
              )}
            </div>
            {/* Site Name, Client, and Mine Type */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-3">
                <Label htmlFor="name" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Site *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="client" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Client *
                </Label>
                <Combobox
                  options={clients.map(userToComboboxOption)}
                  value={formData.client}
                  onValueChange={(value) => handleSelectChange("client", value)}
                  placeholder="SELECT CLIENT"
                  searchPlaceholder="Search clients..."
                  disabled
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="mine_type"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Mine Type *
                </Label>
                <Combobox
                  options={mineTypes.map((mineType) => ({
                    value: mineType.name,
                    label: mineType.name,
                  }))}
                  value={formData.mine_type}
                  onValueChange={(value) => handleSelectChange("mine_type", value)}
                  placeholder="MINE TYPE"
                  searchPlaceholder="Search mine types..."
                  disabled
                />
              </div>
            </div>

            {/* Address, Phone, and Email */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-3">
                <Label
                  htmlFor="contact_name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Site Contact:
                </Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  type="text"
                  placeholder="john doe"
                  value={formData.contact_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="email" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="contact@site.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="phone" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Phone No:
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+91-9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Country, State, Status */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-3">
                <Label htmlFor="country" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Country *
                </Label>
                <Combobox
                  options={countries.map((country) => ({
                    value: country.isoCode,
                    label: country.name,
                  }))}
                  value={countries.find((c) => c.name === selectedCountryName)?.isoCode || ""}
                  onValueChange={handleCountryChange}
                  placeholder="COUNTRY"
                  searchPlaceholder="Search countries..."
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="state" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  State:
                </Label>
                <Combobox
                  options={states.map((state) => ({
                    value: state.isoCode,
                    label: state.name,
                  }))}
                  value={states.find((s) => s.name === selectedStateName)?.isoCode || ""}
                  onValueChange={handleStateChange}
                  placeholder={selectedCountryName ? "STATE" : "SELECT COUNTRY FIRST"}
                  searchPlaceholder="Search states..."
                  disabled={!states.length}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="status" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Status:
                </Label>
                <Combobox
                  options={[
                    { value: "operating", label: "Operating" },
                    { value: "care_maintenance", label: "Care & Maintenance" },
                    { value: "developing", label: "Developing" },
                  ]}
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange("status", value)}
                  placeholder="STATUS"
                  searchPlaceholder="Search status..."
                />
              </div>
            </div>

            {/* Latitude and Longitude */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-3">
                <Label htmlFor="address" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Address *
                </Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  placeholder="123 Mining Road"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="latitude"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Latitude:
                </Label>
                <Input
                  id="latitude"
                  name="latitude"
                  type="number"
                  placeholder="40.7128"
                  value={formData.latitude}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="longitude"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Longitude:
                </Label>
                <Input
                  id="longitude"
                  name="longitude"
                  type="number"
                  placeholder="-74.0060"
                  value={formData.longitude}
                  onChange={handleChange}
                />
              </div>
            </div>


            {/* Submit Buttons */}
            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Link className="border-r rounded-b-none border-gray-0" to="/site">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default EditSitePage;
